import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  MapPin,
  Mail,
  Phone,
  Building2,
  Copy,
  Coins,
  Calendar,
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Trash2,
  Edit3,
} from 'lucide-react';
import { Verification } from '../../../types/verification';
import { useLanguage } from '../../../contexts/LanguageContext';
import { AdminContractHandler } from './AdminContractHandler';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/config';
import toast from 'react-hot-toast';


type Props = {
  verification: Verification;
  onApprove: () => void;
  onReject: () => void;
  onDelete: () => void;
  copiedField: string | null;
  onCopy: (text: string, field: string) => void;
  onUpdateVerification: (updatedVerification: Verification) => void;
};

export const VerificationCard = ({
  verification,
  onApprove,
  onReject,
  onDelete,
  copiedField,
  onCopy,
  onUpdateVerification
}: Props) => {
  const { translations } = useLanguage();
  const [showContractHandler, setShowContractHandler] = useState(false);

  const handleContractSigned = async (signedContractUrl: string) => {
    try {
      const verificationRef = doc(firestore, 'verifications', verification.id);
      await updateDoc(verificationRef, {
        signedContractUrl,
        signedByAdmin: true,
        updatedAt: Date.now()
      });

      // Update local state
      const updatedVerification = {
        ...verification,
        signedContractUrl,
        signedByAdmin: true,
        updatedAt: Date.now()
      };

      onUpdateVerification(updatedVerification);

      toast.success(
        'Contract signed and saved successfully'
      );
    } catch (error) {
      console.error('Error updating verification with signed contract:', error);
      toast.error(
        'Failed to save signed contract'
      );
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return {
          icon: CheckCircle,
          text:
            translations?.verificationApproval?.statuses?.approved ||
            'Approved',
          className: 'bg-green-50 text-green-600 border border-green-200',
        };
      case 'rejected':
        return {
          icon: XCircle,
          text:
            translations?.verificationApproval?.statuses?.rejected ||
            'Rejected',
          className: 'bg-red-50 text-red-600 border border-red-200',
        };
      default:
        return {
          icon: Clock,
          text:
            translations?.verificationApproval?.statuses?.pending || 'Pending',
          className: 'bg-yellow-50 text-yellow-600 border border-yellow-200',
        };
    }
  };

  const StatusBadge = getStatusBadge(verification.status);
  const StatusIcon = StatusBadge.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-sm overflow-hidden"
    >
      <div className="p-4 md:p-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-50 rounded-lg">
                <FileText className="text-primary-500" size={24} />
              </div>
              <div>
                <h3 className="font-medium text-lg">
                  {verification.userInfo.fullName} ({verification.userName})
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <div
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${StatusBadge.className}`}
                  >
                    <StatusIcon size={16} />
                    <span className="text-sm font-medium">
                      {StatusBadge.text}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* User Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div className="flex items-center gap-2 text-gray-600">
                <User size={16} />
                <span className="capitalize">
                  {translations?.verificationApproval?.userTypes?.[
                    verification.userType
                  ] || verification.userType}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin size={16} />
                <span>{verification.userInfo.country}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 break-all">
                <Mail size={16} />
                <span>{verification.userInfo.email}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Phone size={16} />
                <span>{verification.userInfo.mobileNumber}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Building2 size={16} />
                <span>{verification.userInfo.agent}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar size={16} />
                <span className="text-sm">
                  {translations?.verificationApproval?.createdAt || 'Created'}:{' '}
                  {new Date(verification.createdAt).toLocaleString()}
                </span>
              </div>
              {verification.userInfo.birthDate && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar size={16} />
                  <span>
                    {'Birth Date'}:{' '}
                    {new Date(verification.userInfo.birthDate).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
        

            {/* View Signed Contract */}
            {verification.signedContractUrl && (
              <a
                href={verification.signedContractUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
              >
                <ExternalLink size={18} />
                <span>
                  {'View Signed Contract'}
                </span>
              </a>
            )}

            {/* Handle Contract (for admins) */}
            {verification.status === 'pending' && verification.contractUrl && (
              <button
                onClick={() => setShowContractHandler(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Edit3 size={18} />
                <span>
                  {'Handle Contract'}
                </span>
              </button>
            )}

            {/* Delete Button */}
            <button
              onClick={onDelete}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
            >
              <Trash2 size={18} />
              <span>
                {translations?.verificationApproval?.buttons?.delete ||
                  'Delete'}
              </span>
            </button>

            {/* Approve/Reject Actions */}
            {verification.status === 'pending' && (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (!verification.contractUrl) {
                      toast.error(
                        'No contract available for this verification'
                      );
                      return;
                    }
                    if (!verification.signedByAdmin) {
                      toast.error(
                        'Please download, sign, and upload the contract before approving the verification'
                      );
                      setShowContractHandler(true);
                      return;
                    }
                    onApprove();
                  }}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  <CheckCircle size={18} />
                  <span>
                    {translations?.verificationApproval?.buttons?.approve ||
                      'Approve'}
                  </span>
                </button>
                <button
                  onClick={onReject}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  <XCircle size={18} />
                  <span>
                    {translations?.verificationApproval?.buttons?.reject ||
                      'Reject'}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Bank Info Section */}
        {verification.userType === 'organizer' && verification.bankInfo && (
          <div className="mt-6 bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Coins size={16} className="text-primary-500" />
              <span>
                {translations?.verificationApproval?.bankInfo?.title ||
                  'Bank Information'}
              </span>
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(verification.bankInfo).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="break-all">{value}</span>
                    <button
                      onClick={() => onCopy(value, key)}
                      className={`p-1 rounded-full hover:bg-gray-100 ${
                        copiedField === key ? 'text-green-500' : 'text-gray-400'
                      }`}
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Admin Contract Handler Modal */}
      {showContractHandler && (
        <AdminContractHandler
          verification={verification}
          onClose={() => setShowContractHandler(false)}
          onContractSigned={handleContractSigned}
        />
      )}
    </motion.div>
  );
};
